import { baseApi } from "./api";

// Note: saveLV and uploadLVFile send FormData bodies. The axiosBaseQuery
// passes `body` straight to axios, which auto-sets Content-Type with the
// multipart boundary when given a FormData instance. We deliberately do not
// set Content-Type here - manual `multipart/form-data` would omit the
// boundary and break the request.

export const lvApi = baseApi.injectEndpoints({
	endpoints: (build) => ({
		getProjectLVs: build.query({
			query: (projectId) => ({
				url: `projects/${projectId}/lvs`,
			}),
			providesTags: (result, _e, projectId) => [
				{ type: "LV", id: `LIST-${projectId}` },
				...((result?.items ?? []).map((lv) => ({
					type: "LV",
					id: lv.id,
				}))),
			],
		}),
		getProjectLVCount: build.query({
			query: (projectId) => ({
				url: `projects/${projectId}/lvs/count`,
			}),
			providesTags: (_r, _e, projectId) => [
				{ type: "LV", id: `count-${projectId}` },
			],
		}),
		getLVById: build.query({
			query: ({ projectId, lvId }) => ({
				url: `projects/${projectId}/lvs/${lvId}`,
			}),
			providesTags: (_r, _e, { lvId }) => [
				{ type: "LV", id: lvId },
			],
		}),
		getLVPositions: build.query({
			query: ({ projectId, lvId, ...params }) => {
				const hasParams = params && Object.keys(params).length > 0;
				return {
					url: `projects/${projectId}/lvs/${lvId}/positions`,
					params: hasParams ? params : undefined,
				};
			},
			providesTags: (_r, _e, { lvId }) => [
				{ type: "LV", id: `positions-${lvId}` },
			],
		}),
		getLVsByTicketCategory: build.query({
			query: ({ projectId, ticketCategoryId }) => ({
				url: `projects/${projectId}/ticket-categories/${ticketCategoryId}/lvs`,
			}),
			providesTags: (_r, _e, { ticketCategoryId }) => [
				{ type: "LV", id: `category-${ticketCategoryId}` },
			],
		}),
		saveLV: build.mutation({
			query: ({ projectId, formData }) => ({
				url: `projects/${projectId}/lvs`,
				method: "POST",
				body: formData,
			}),
			invalidatesTags: (_r, _e, { projectId }) => [
				{ type: "LV", id: `LIST-${projectId}` },
				{ type: "LV", id: `count-${projectId}` },
				{ type: "TicketCategory", id: `lv-status-${projectId}` },
			],
		}),
		updateLV: build.mutation({
			query: ({ projectId, lvId, ...body }) => ({
				url: `projects/${projectId}/lvs/${lvId}`,
				method: "PUT",
				body,
			}),
			invalidatesTags: (_r, _e, { projectId, lvId }) => [
				{ type: "LV", id: lvId },
				{ type: "LV", id: `LIST-${projectId}` },
			],
		}),
		deleteLV: build.mutation({
			query: ({ projectId, lvId }) => ({
				url: `projects/${projectId}/lvs/${lvId}`,
				method: "DELETE",
			}),
			invalidatesTags: (_r, _e, { projectId, lvId }) => [
				{ type: "LV", id: lvId },
				{ type: "LV", id: `LIST-${projectId}` },
				{ type: "LV", id: `count-${projectId}` },
				{ type: "TicketCategory", id: `lv-status-${projectId}` },
			],
		}),
		uploadLVFile: build.mutation({
			query: ({ projectId, lvId, formData }) => ({
				url: `projects/${projectId}/lvs/${lvId}/upload`,
				method: "PUT",
				body: formData,
			}),
			invalidatesTags: (_r, _e, { projectId, lvId }) => [
				{ type: "LV", id: lvId },
				{ type: "LV", id: `positions-${lvId}` },
				{ type: "LV", id: `LIST-${projectId}` },
			],
		}),
		updatePositionCostGroups: build.mutation({
			query: ({ projectId, lvId, body }) => ({
				url: `projects/${projectId}/lvs/${lvId}/positions/cost-groups`,
				method: "POST",
				body,
			}),
			invalidatesTags: (_r, _e, { lvId }) => [
				{ type: "LV", id: `positions-${lvId}` },
			],
		}),
		updatePositionUnitPrice: build.mutation({
			query: ({ projectId, lvId, positionId, unitPrice }) => ({
				url: `projects/${projectId}/lvs/${lvId}/positions/${positionId}/unit-price`,
				method: "PATCH",
				body: { unitPrice },
			}),
			invalidatesTags: (_r, _e, { lvId }) => [
				{ type: "LV", id: `positions-${lvId}` },
			],
		}),
	}),
});

export const {
	useGetProjectLVsQuery,
	useGetProjectLVCountQuery,
	useGetLVByIdQuery,
	useGetLVPositionsQuery,
	useLazyGetLVPositionsQuery,
	useGetLVsByTicketCategoryQuery,
	useSaveLVMutation,
	useUpdateLVMutation,
	useDeleteLVMutation,
	useUploadLVFileMutation,
	useUpdatePositionCostGroupsMutation,
	useUpdatePositionUnitPriceMutation,
} = lvApi;
